"use client";

import { useContext, useMemo, useState } from "react";
import {
  Avatar,
  Button,
  Label,
  TextInput,
  Modal,
  Checkbox,
  Badge,
} from "flowbite-react";
import { AiOutlineClose } from "react-icons/ai";
import {
  MdTransferWithinAStation,
  MdTranslate,
  MdContactPage,
  MdGroup,
  MdPerson,
  MdExitToApp,
} from "react-icons/md";
import { FaUserTie, FaUser } from "react-icons/fa";
import { ChatContext, ChatContextInterface } from "../../../components/MSRContext"; // ⬅ adjust if needed
import { createRequestControlMessage } from "../../../utils/cmsgBuilder";
import generateRandomString from "../../../MSRP/utilities/generators";
import Global from "../../../globals/globals";
import { getAvailableAgents, Agent } from "../../../globals/availableAgents";
import { toast } from "react-toastify";
import type { Participant } from "@/types/chat.types";

type ChatBarProps = {
  open: boolean;
  onClose: () => void;
  currentRoom:any
};

export function ChatBarDrawer({ open, onClose, currentRoom }: ChatBarProps) {
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [isReasonInputOpen, setIsReasonInputOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [reason, setReason] = useState("");

  const {
    wrapper,
    updateAgentList,
    handleSessionActivation,
  } = useContext(ChatContext) as ChatContextInterface;

  const availableAgents = useMemo(() => {
    const all = getAvailableAgents();
    if (!currentRoom) return all;
    const participants = Array.isArray(currentRoom.participants)
      ? currentRoom.participants
      : [];
    const excluded = new Set<string>([
      ...participants,
      currentRoom.username,
      Global.username + Global.companyDomain,
    ]);
    return all.filter((a) => !excluded.has(a.name));
  }, [currentRoom]);

  const handleOpenTransferModal = () => {
    updateAgentList("agent-available");
    setIsTransferModalOpen(true);
  };

  const handleCloseTransferModal = () => {
    setIsTransferModalOpen(false);
    setSelectedAgent(null);
  };

  const handleAgentSelection = (agent: Agent) => {
    setSelectedAgent(agent);
  };

  const handleOpenReasonInput = () => {
    if (!selectedAgent) {
      toast.warning("Please select an agent to transfer the chat.");
      return;
    }
    setIsTransferModalOpen(false);
    setIsReasonInputOpen(true);
  };

  const handleTransferChat = () => {
    if (!selectedAgent || !reason.trim()) {
      toast.warning("Please provide a reason for the transfer.");
      return;
    }
    const targetAgent = selectedAgent.name;

    wrapper?.sendMsg(
      createRequestControlMessage(
        generateRandomString(16),
        "session-transfer",
        `from-name: ${Global.username + Global.companyDomain}, target-name: ${targetAgent}, transferee-name:${currentRoom?.username}, reason: ${reason}, history-added: true`
      ),
      "application/rechat.cntl+json"
    );

    setIsReasonInputOpen(false);
    setSelectedAgent(null);
    setReason("");

    if (currentRoom) {
      handleSessionActivation(currentRoom.username, false);
    }
  };

  const handleCancelTransfer = () => {
    setIsReasonInputOpen(false);
    setReason("");
    setSelectedAgent(null);
  };

  const title =
    currentRoom?.type === "agent-to-agent" || currentRoom?.type === "c2a"
      ? currentRoom?.username.split("@")[0]
      : currentRoom?.isGroup
      ? currentRoom?.username.replace("grpcht:", "")
      : currentRoom?.username?.replace("conf:", "");

  return (
    <>
      {/* Drawer panel */}
      <div
        className={`fixed inset-0 z-40 transition-transform duration-300 ease-in-out ${
          open ? "translate-x-0" : "translate-x-full"
        } pointer-events-none`}
      >
        <div className="absolute right-0 h-full bg-white shadow-lg pointer-events-auto w-80 dark:bg-gray-800">
          <div className="flex items-center justify-between p-4 border-b dark:border-gray-600">
            <h2 className="text-lg font-semibold dark:text-white">Chat Actions</h2>
            <button onClick={onClose}>
              <AiOutlineClose className="w-5 h-5 dark:text-white" />
            </button>
          </div>

          <div className="p-4 space-y-4 overflow-y-auto h-full pb-20">
            <div className="flex items-center mb-4">
              <Avatar
                alt="User avatar"
                img="/images/users/thomas-lean.png" // ⬅ remove /public prefix for Next.js public/
                rounded
                size="md"
                className="mr-3"
              />
              <div>
                <p className="font-medium dark:text-white">{title}</p>
              </div>
            </div>

            {/* Participants Section */}
            {Array.isArray(currentRoom?.participants) && currentRoom.participants.length > 0 && (
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4">
                  <div className="flex items-center mb-3">
                    <MdGroup className="w-5 h-5 mr-2 text-gray-600 dark:text-gray-300" />
                    <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                      Participants ({currentRoom.participants.length})
                    </h3>
                  </div>
                  <div
                    className={`space-y-2 ${
                      currentRoom.participants.length > 7
                        ? "max-h-64 overflow-y-auto"
                        : ""
                    }`}
                  >
                    {currentRoom.participants.map((participant: Participant) => {
                      const isClient = participant.user_type === 1;
                      const isAgent = participant.user_type === 3;
                      const hasExited = participant.exit_time !== null;
                      const isAdmin = participant.is_admin === 1;

                      // Get display name
                      const displayName = isClient
                        ? (participant.details as any).client_username
                        :  (participant.details as any).agent_username;

                      return (
                        <div
                          key={participant.user_id}
                          className={`flex items-center p-2 bg-white dark:bg-gray-600 rounded-md shadow-sm hover:shadow-md transition-shadow duration-200 ${
                            hasExited ? 'opacity-60' : ''
                          }`}
                        >
                          <div className={`flex items-center justify-center w-8 h-8 rounded-full mr-3 flex-shrink-0 ${
                            isClient
                              ? 'bg-green-100 dark:bg-green-900'
                              : 'bg-blue-100 dark:bg-blue-900'
                          }`}>
                            {isClient ? (
                              <FaUser className="w-4 h-4 text-green-600 dark:text-green-300" />
                            ) : (
                              <FaUserTie className="w-4 h-4 text-blue-600 dark:text-blue-300" />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                {participant.is_current_user ? "You" : displayName}
                              </p>
                              {isAdmin && (
                                <Badge color="purple" size="xs">Admin</Badge>
                              )}
                              {isClient && (
                                <Badge color="success" size="xs">Client</Badge>
                              )}
                              {isAgent && (
                                <Badge color="info" size="xs">Agent</Badge>
                              )}
                            </div>
                            {hasExited && (
                              <div className="flex items-center gap-1 mt-1">
                                <MdExitToApp className="w-3 h-3 text-red-500" />
                                <span className="text-xs text-red-500 dark:text-red-400">Exited</span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

            <div className="space-y-3">
              {!currentRoom?.isAgent &&
                !currentRoom?.isConference &&
                !currentRoom?.isGroup && (
                  <Button
                    color={"chatbar"}
                    className="w-full"
                    onClick={handleOpenTransferModal}
                    disabled={!currentRoom?.isSessionActive}
                  >
                    <MdTransferWithinAStation className="w-5 h-5 mr-2" />
                    Transfer Chat
                  </Button>
                )}
              <Button color="chatbar" className="w-full">
                <MdTranslate className="w-5 h-5 mr-2" />
                Translate Chat
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Select Agent Modal */}
      <Modal
        onClose={handleCloseTransferModal}
        popup
        show={isTransferModalOpen}
        size="xl"
        theme={{
          content: {
            inner:
              "relative flex max-h-[90vh] flex-col rounded-lg bg-white shadow dark:bg-gray-800",
          },
        }}
      >
        <Modal.Header>Please select an Agent to Transfer:</Modal.Header>
        <Modal.Body>
          {availableAgents.length === 0 ? (
            <legend className="mb-4 text-gray-600 dark:text-gray-400">
              No available agents at the moment.
            </legend>
          ) : (
            <fieldset className="flex flex-col max-w-md gap-4 my-6">
              {availableAgents.map((agent: Agent) => (
                <div key={agent.id} className="flex items-center gap-2">
                  <Checkbox
                    id={String(agent.id)}
                    name={agent.name}
                    value={agent.name}
                    checked={selectedAgent?.id === agent.id}
                    onChange={() => handleAgentSelection(agent)}
                  />
                  <Label htmlFor={String(agent.id)}>
                    {agent.name.split("@")[0]}
                  </Label>
                </div>
              ))}
            </fieldset>
          )}
          <div className="flex items-center justify-center space-x-4">
            {availableAgents.length > 0 && (
              <Button onClick={handleOpenReasonInput}>Next</Button>
            )}
            <Button color="primary" type="submit" onClick={handleCloseTransferModal}>
              Cancel
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Reason Modal */}
      <Modal
        onClose={handleCancelTransfer}
        popup
        show={isReasonInputOpen}
        size="xl"
        theme={{
          content: {
            inner:
              "relative flex max-h-[90vh] flex-col rounded-lg bg-white shadow dark:bg-gray-800",
          },
        }}
      >
        <Modal.Header>
          Transfer Chat to {selectedAgent?.name.split("@")[0]}
        </Modal.Header>
        <Modal.Body>
          <div className="my-6">
            <Label
              htmlFor="reason"
              className="block mb-2 text-gray-900 dark:text-white"
            >
              Reason for transferring:
            </Label>
            <TextInput
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter reason for transfer"
              className="w-full"
              required
            />
          </div>
          <div className="flex items-center justify-center space-x-4">
            <Button onClick={handleTransferChat}>Transfer Chat</Button>
            <Button color="primary" type="submit" onClick={handleCancelTransfer}>
              Cancel
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}
